'use client';

import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useMemo } from "react";
import Image from "next/image";
import useFn from "@/hooks/use-fn";
import { track } from "@/utils/track";
import { isLoginAtom, transferPackageIsShowAtom, transferPackageShowAtom } from "@/store";
import { Button } from '@/components/ui/button';
import { useContactCustomerService } from "@/components/contact-customer-service/use-contact-customer-service";
import CustomerServiceImg from '../images/icon-service.png';
import { openLink } from "@/utils/bridge-cmd";
import { showModalPhoneEdit } from '@/features/user-center/components/phone-edit';
import useUserInfo from '@/features/user-center/hooks/useUserInfo';
import type { TransferPopupConfigRes } from "@/types/api/user/transfer-popup-config";
import { TRANSFER_TYPE_MAP } from "../constants";

interface TransferPackageModalProps extends TransferPopupConfigRes {
  appId: string;
}

const TransferPackageModal = (props: TransferPackageModalProps) => {
  const setShouldRender = useSetAtom(transferPackageShowAtom);
  const setIsShow = useSetAtom(transferPackageIsShowAtom);
  const {
    transferType, customerServiceEnabled, downloadLink, popupInfo,
    targetPackageName, appId
  } = props;
  const { buttonText, iconUrl } = popupInfo || {
    buttonText: '',
    iconUrl: '',
    popupType: -1,
    popupCount: 0,
  };
  const { freshUserInfo, userInfo: user } = useUserInfo();
  const isLogin = useAtomValue(isLoginAtom);
  const transfer_type = transferType === TRANSFER_TYPE_MAP.FORCE ? 'forced transfer' : 'non-forced transfer';

  const { handleContact } = useContactCustomerService({ source: 'Package Transfer Page' });
  const handleContactCustomer = useFn(() => {
    handleContact();
    track("package_transfer_page_button_click", {
      transfer_type,
      package_name: appId,
      to_package_name: targetPackageName,
      button_name: 'Customer Service'
    });
  });

  const handleBindPhone = useCallback(async () => {
    const result = await showModalPhoneEdit({
      className: 'z-[10001]',
      overlayClassName: 'z-[10001]',
    });
    if (result) {
      freshUserInfo();
    }
  }, [freshUserInfo]);

  const openPage = useCallback(() => {
    if (!downloadLink) {
      return;
    }
    const hasAccount = user?.phone || user?.email;
    // 已登录且未绑定手机和邮箱
    if (isLogin && !hasAccount) {
      handleBindPhone();
    }
    openLink(downloadLink);
    track("package_transfer_page_button_click", {
      transfer_type,
      package_name: appId,
      to_package_name: targetPackageName,
      button_name: 'download'
    });
  }, [appId, downloadLink, handleBindPhone, isLogin, targetPackageName, transfer_type, user?.email, user?.phone]);

  const maxHeight = useMemo(() => {
    let offset = 260;
    if (!customerServiceEnabled) {
      offset -= 76;
    }
    return `${window.innerHeight - offset}px`;
  }, [customerServiceEnabled]);

  const handleClose = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShouldRender(false);
    setIsShow(false);
    track("package_transfer_page_button_click", {
      transfer_type,
      package_name: appId,
      to_package_name: targetPackageName,
      button_name: 'close'
    });
  }, [appId, setIsShow, setShouldRender, targetPackageName, transfer_type]);

  // 如果缺少必要数据，不渲染组件
  if (!iconUrl || !downloadLink) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[10000] bg-black/85 py-[20px] flex flex-col justify-center items-center" style={{ pointerEvents: 'auto' }} onClick={(e: React.MouseEvent) => e.stopPropagation()}>

      <div className='w-full px-[15px] flex flex-col items-center'>
        <div className='w-full flex justify-end items-center mb-[25px]'>
          {
            customerServiceEnabled && (
              <Image src={CustomerServiceImg} width={36} height={36} alt=""
                onClick={handleContactCustomer} />
            )
          }
          {
            transferType === TRANSFER_TYPE_MAP.UNFORCE && <div className="ml-[15px] tap-target-34 flex items-center justify-center" onClick={handleClose}><Image
              src="/assets/images/ic_close.png"
              alt="close"
              width={13}
              height={13}
              className="h-[13px]"

            /></div>
          }

        </div>
        <div className='overflow-hidden w-[360px] max-w-[100%]' style={{
          maxHeight
        }}>
          <Image
            src={iconUrl}
            width={0}
            height={0}
            alt=""
            style={{
              width: '100%',
              height: 'auto'
            }}
          />
        </div>
        {buttonText && <Button className='mt-[25px] w-[280px] h-[40px] btn-warning' onClick={openPage}>
          {buttonText || ''}
        </Button>}
      </div>
    </div>
  );
};

export default TransferPackageModal;